import React, { useMemo, useState } from 'react';
import { Empty } from 'antd';
import { Bars, Card, Legend, LegendOrientations, Text } from '@clockout-dev/design-elements';
import DashboardController from '../../dashboard/dashboard.controller';
import { formatCompactCurrency } from '../../../utils/numbers';
import CardSkeleton from '../cardSkeleton/CardSkeleton';
import styles from './styles/toggle-bar-chart.module.scss';

function ToggleBarChart({
    title,
    subtitle,
    toggles = [],
    secondaryToggles = [],
    series = [],
    data = {},
    defaultToggle,
    defaultSecondaryToggle,
    activeToggle: controlledToggle,
    onToggleChange,
    showLegend,
    isCurrency = false,
    loading = false,
}) {
    const hasSecondary = secondaryToggles.length > 0;

    const initialToggle = defaultToggle ?? toggles[toggles.length - 1]?.key ?? null;
    const initialSecondary = defaultSecondaryToggle ?? secondaryToggles[0]?.key ?? null;

    const [internalToggle, setInternalToggle] = useState(initialToggle);
    const [activeSecondary, setActiveSecondary] = useState(initialSecondary);

    const isControlled = controlledToggle !== undefined && onToggleChange;
    const activeToggle = isControlled ? controlledToggle : internalToggle;
    const setActiveToggle = isControlled ? onToggleChange : setInternalToggle;

    const chartData = useMemo(() => {
        const base = hasSecondary ? (data?.[activeToggle] ?? {}) : data;
        const key = hasSecondary ? activeSecondary : activeToggle;
        return DashboardController.buildToggleBarChartData(base, key, series);
    }, [data, activeToggle, activeSecondary, hasSecondary, series]);

    const barsSeries = useMemo(
        () => series.map(({ label, color }) => ({ label, color })),
        [series]
    );

    const hasData = chartData.length > 0;
    const legendVisible = showLegend ?? series.length > 1;

    const activeToggleObj = toggles.find(({ key }) => key === activeToggle);
    const currencyActive = isCurrency || activeToggleObj?.isCurrency || false;
    const formatValue = currencyActive
        ? (value) => formatCompactCurrency(value ?? 0)
        : (value) => (value ?? 0).toLocaleString();

    const renderToggle = (items, active, setActive) => (
        <div className={styles.toggle}>
            {items.map(({ key, label }) => (
                <button
                    key={key}
                    type="button"
                    className={`${styles.toggleButton} ${active === key ? styles.toggleActive : ''}`}
                    onClick={() => setActive(key)}
                >
                    {label}
                </button>
            ))}
        </div>
    );

    return (
        <Card className={styles.card}>
            <div className={styles.header}>
                <div className={styles.heading}>
                    <Text as="span" className={styles.title}>{title}</Text>
                    {subtitle ? <Text as="span" className={styles.subtitle}>{subtitle}</Text> : null}
                </div>
                <div className={styles.controls}>
                    <div className={styles.toggleRow}>
                        {toggles.length > 0 && renderToggle(toggles, activeToggle, setActiveToggle)}
                        {hasSecondary ? renderToggle(secondaryToggles, activeSecondary, setActiveSecondary) : null}
                    </div>
                    {legendVisible ? (
                        <Legend
                            items={series.map(({ label, color }) => ({ label, color }))}
                            orientation={LegendOrientations.HORIZONTAL}
                            className={styles.legend}
                        />
                    ) : null}
                </div>
            </div>
            <div className={styles.chartContainer}>
                {loading ? (
                    <CardSkeleton minHeight={280} rows={6} />
                ) : hasData ? (
                    <Bars
                        data={chartData}
                        series={barsSeries}
                        height={280}
                        formatValue={formatValue}
                    />
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data for this period" />
                )}
            </div>
        </Card>
    );
}

export default ToggleBarChart;
